import { EnvelopeIcon } from "@phosphor-icons/react/dist/csr/Envelope";
import { SealCheckIcon } from "@phosphor-icons/react/dist/csr/SealCheck";
import {
  SHEET_APPROVAL_TYPE,
  type IPayrollSheetApprovalAccessKey,
  type SheetApprovalType,
} from "@/payroll/payroll.d";

/**
 * The two approval gates, in the order a payroll actually passes them: a
 * department is cleared for its technicians to check their own pay and raise
 * anything wrong, then payroll itself is declared final. One icon per gate,
 * used at EVERY size — tab strip, popover cells, email warning — so a reader
 * learns the two marks once and then recognises them everywhere.
 *
 * Envelope vs rosette rather than two check variants: both gates are "approved"
 * states, so the glyph has to carry which gate, not whether it is met (fill
 * carries that).
 *
 * `description` is what the reader sees BEFORE anyone has settled the gate, in
 * the row with the switch. It names what the gate DOES, in the present tense, and
 * deliberately claims nothing about the current state:
 *
 *   - Written as the finished state it asserted the opposite of the truth. An
 *     unapproved "Payroll finalized" row read "The pay on these sheets is final."
 *     underneath an off switch.
 *   - Written as an instruction ("Switch on to...") it just repeated the toggle's
 *     own hover hint, which already says "Switch on to approve."
 *
 * Once a gate IS settled the row shows who settled it instead.
 */
export const APPROVAL_GATES = [
  {
    type: SHEET_APPROVAL_TYPE.readyForTechApproval,
    label: "Ready for PayPacks",
    description: "Clears the PayPacks to go out.",
    Icon: EnvelopeIcon,
  },
  {
    type: SHEET_APPROVAL_TYPE.payrollFinalized,
    label: "Payroll finalized",
    description: "Marks the pay as final.",
    Icon: SealCheckIcon,
  },
] as const;

export const getApprovalGate = (type: SheetApprovalType) =>
  APPROVAL_GATES.find((gate) => gate.type === type) ?? APPROVAL_GATES[0];

/**
 * Anything carrying the two gates. Narrowed to `approvals` on purpose: the two
 * read models describe a unit differently either side of it (one has an id and
 * an authority flag, the other a key string), and the mark cares about neither.
 *
 * Nullable because the request dialog joins gate state on by access key, and a
 * unit that join does not cover has no state rather than an unapproved one. Both
 * read as unsigned here, which is the safe direction: never claim a sign-off the
 * data did not give.
 */
export type ApprovalGateHolder = {
  approvals: IPayrollSheetApprovalAccessKey["approvals"] | null;
};

/** Whether one of a unit's two gates is met. */
export const isApproved = (
  accessKey: ApprovalGateHolder,
  gate: SheetApprovalType,
) => accessKey.approvals?.[gate].approved ?? false;

export const getApprovedGateCount = (accessKey: ApprovalGateHolder) =>
  APPROVAL_GATES.filter((gate) => isApproved(accessKey, gate.type)).length;

/**
 * What the mark on a tab is actually showing, spelled out. Written per state
 * rather than assembled from the gate labels, because the useful sentence is
 * what the reader can DO about it, and that differs by state in a way a
 * generated "X: approved. Y: not approved yet" never gets to.
 */
export const describeMark = (accessKey: ApprovalGateHolder) => {
  const ready = isApproved(accessKey, SHEET_APPROVAL_TYPE.readyForTechApproval);
  const finalized = isApproved(accessKey, SHEET_APPROVAL_TYPE.payrollFinalized);

  if (ready && finalized) {
    return "Fully approved. The technicians can check their pay, and the pay is final.";
  }
  if (ready) {
    return "Ready for PayPacks. The technicians can check their pay. It is not final yet.";
  }
  if (finalized) {
    return "Payroll finalized. The pay is final, but the technicians have not been cleared to check it.";
  }
  return "Not approved yet. Approval is required for these sheets.";
};
